import PostsGrid from 'Modules/Plans/components/PlatformPosts/PostsGrid/PostsGrid';
import PlatformPost from 'Modules/Plans/components/PlatformPosts/PlatformPost/PlatformPost';
import { mapTikTokPostToPlatformPostData } from 'Modules/Plans/components/PlatformPosts/PlatformPost/helpers';
import useSinglePlayback from 'Hooks/vidstack/useSinglePlayback';
import { useState } from 'react';
import PlatformPostsFilters, {
    Filters,
    CampaignOption,
} from 'Modules/Plans/components/PlatformPosts/PlatformPostsFilters/PlatformPostsFilters';
import useCampaigns from '../../Campaigns/useCampaigns';
import range from 'lodash/range';
import { MessageArea } from '@round/whitelabel-ui';
import { useReport } from '@round/creatorbase-helpers/api-hooks';
import styles from './TikTokTab.module.css';
import usePlatformPosts from 'Modules/Plans/hooks/queries/posts/usePlatformPosts';
import useOnScrolledPastThreshold from 'Hooks/scroll/useOnScrolledPastThreshold';
import { CountRangeLabel } from '@round/whitelabel-ui';
import useReportId from '../../hooks/useReportId';

const PAGE_SIZE = 50;

const TikTokPlatformPosts = () => {
    const reportId = useReportId();
    const [filters, setFilters] = useState<Filters>({});
    const { campaignsData, projectsData, brandsData } = useCampaigns('tiktok');
    const { data: report, isLoading: isReportLoading } = useReport(reportId);

    const campaignOptions: CampaignOption[] =
        campaignsData.data?.results.map((c) => {
            const project = projectsData.get(c.project_id) ?? undefined;
            const brand = project ? brandsData[project?.brand_id]?.data ?? undefined : undefined;
            return {
                value: c.id,
                label: c.name,
                managedBy: c.managed_by,
                imageUrl: project?.image || '',
                brandName: brand?.name,
                projectName: project?.name,
            };
        }) ?? [];

    const { data, isLoading, errorMessage, status, userStatsCache, hasNextPage, isFetchingNextPage, fetchNextPage } =
        usePlatformPosts(
            {
                platform: 'tiktok',
                include_post_details: true,
                report_id: reportId,
                status: 'live',
                page_size: PAGE_SIZE,
                ...filters,
            },
            // posts fetch even for unavailable report (e.g. no access to client)
            { enabled: !!report?.id }
        );

    const posts = data?.pages.flatMap((p) => p.results) ?? [];
    const hasPosts = Boolean(status === 'success' && posts.length);
    const isPostsLoading = isReportLoading || isLoading;
    const handleSinglePlayback = useSinglePlayback();
    const onEndReached = useOnScrolledPastThreshold({ threshold: 150 });

    return (
        <>
            <PlatformPostsFilters
                filters={filters}
                onChange={setFilters}
                campaignOptions={campaignOptions}
                reportId={reportId}
            />

            <CountRangeLabel
                className={styles.countRange}
                pageParams={data?.pageParams as number[]}
                count={data?.pages[0]?.count}
                pageSize={PAGE_SIZE}
                isLoading={isPostsLoading}
            />

            {((!hasPosts && !isPostsLoading) || !!errorMessage) && (
                <MessageArea appearance={errorMessage ? 'error' : undefined}>
                    <span>{errorMessage || 'No posts yet.'}</span>
                </MessageArea>
            )}

            <PostsGrid
                className={styles.postsGrid}
                onScroll={onEndReached(() => {
                    if (hasNextPage && !isFetchingNextPage && !isLoading) {
                        fetchNextPage();
                    }
                })}
            >
                {posts.map((post) => (
                    <PlatformPost
                        key={post.id}
                        data={mapTikTokPostToPlatformPostData(
                            post,
                            userStatsCache.find((c) => c.user_id === post.tiktok_details?.account_id)
                        )}
                        onPlay={handleSinglePlayback}
                    />
                ))}

                {(isPostsLoading || isFetchingNextPage) &&
                    range(0, PAGE_SIZE).map((index) => <PlatformPost.LoadingView key={index} />)}
            </PostsGrid>
        </>
    );
};

export default TikTokPlatformPosts;
